import { httpClient } from "../../../shared/api";

function cleanQuery(query = {}) {
  const out = {};
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    out[k] = v;
  });
  return out;
}

export const defectsApi = {
  getById(defectId) {
    return httpClient.get(`/defects/${defectId}`);
  },

  create(payload) {
    return httpClient.post("/defects", payload);
  },

  update(defectId, payload) {
    return httpClient.patch(`/defects/${defectId}`, payload);
  },

  updateStatus(defectId, status) {
    return httpClient.patch(`/defects/${defectId}`, { status });
  },

  // Deliberately a SEPARATE method from updateStatus() above, hitting
  // POST /defects/{id}/transition instead of the generic PATCH — that
  // endpoint validates the move server-side (409 if not a legal transition,
  // matching ALLOWED_TRANSITIONS) and doesn't touch epic_id at all, unlike
  // the generic PATCH (which re-resolves epic_id on every call and can
  // 422 an otherwise-valid status change on a legacy epic-less defect).
  // Used by the Issues table's inline status edit; the Kanban board's own
  // drag-and-drop still uses updateStatus() above — left as-is, a known,
  // separate inconsistency, not touched by this change.
  transitionStatus(defectId, toStatus, reason) {
    return httpClient.post(`/defects/${defectId}/transition`, {
      to_status: toStatus,
      ...(reason ? { reason } : {}),
    });
  },

  list(params = {}) {
    return httpClient.get("/defects", { query: cleanQuery(params) });
  },

  // Server-side CSV export of every defect matching the filters — not capped
  // at the /defects list default/max limit. httpClient returns response text
  // as-is for non-JSON content types (text/csv here).
  exportCsv(params = {}) {
    return httpClient.get("/defects/export", { query: cleanQuery(params) });
  },

  dashboard(params = {}) {
    return httpClient.get("/defects/dashboard-data", { query: cleanQuery(params) });
  },

  statusCounts(params = {}) {
    return httpClient.get("/defects/status-counts", { query: cleanQuery(params) });
  },

  executiveSummary() {
    return httpClient.get("/defects/executive-summary");
  },

  count(params = {}) {
    return httpClient.get("/defects/count", { query: cleanQuery(params) });
  },

  listUsers() {
    return httpClient.get("/users/dropdown");
  },

  listTeams() {
    return httpClient.get("/teams");
  },

  listCustomersProjects(userId) {
    const query = cleanQuery({ user_id: userId, active_only: true });
    return httpClient.get("/customers-projects", { query });
  },

  listTasks() {
    // page_size=2000 (the API max) — this feeds the defect drawer's
    // client-side Task search picker, which needs the full set of tasks
    // for the selected project, not just the default 50-row page.
    return httpClient.get("/tasks?view=all&status_in=Open,In Progress,Completed,Cancelled,On Hold,NA&page_size=2000");
  },

  listFeatures() {
    return httpClient.get("/features");
  },

  listSubFeatures() {
    return httpClient.get("/sub-features");
  },

  listDependencyRequests() {
    return httpClient.get("/dependency-requests");
  },

  listEpics(projectId, { includeClosed = false } = {}) {
    if (!projectId) return Promise.resolve([]);
    return httpClient.get("/epics", {
      query: { project_id: projectId, include_closed: includeClosed },
    });
  },

  listDiscussions(defectId) {
    return httpClient.get(`/discussions/defect/${defectId}`);
  },

  listStatusHistory(defectId) {
    return httpClient.get(`/defects/${defectId}/status-history`);
  },

  listAuditLogs(defectId) {
    return httpClient.get(`/audit-logs/defects/${defectId}`);
  },

  listReopenHistory(defectId) {
    return httpClient.get(`/defects/${defectId}/reopen-history`);
  },

  listTimeEntries(defectId) {
    return httpClient.get("/daily-reports", { query: { ref_type: "defect", ref_id: defectId } });
  },

  createDiscussion(payload) {
    return httpClient.post("/discussions", payload);
  },

  deleteDiscussion(discussionId) {
    return httpClient.delete(`/discussions/${discussionId}`);
  },

  listAttachments(defectId) {
    return httpClient.get(`/attachments/defect/${defectId}`);
  },

  deleteAttachment(attachmentId) {
    return httpClient.delete(`/attachments/${attachmentId}`);
  },

  uploadAttachment(formData) {
    return httpClient.post("/attachments/upload", formData);
  },
};
